import type { Customer } from '../../../core/types/account'
import type { CustomerPatch } from '../../../core/types/requests'

export type ProfileSection = 'persoon' | 'adres' | 'verjaardag' | 'betalen'

export const MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'] as const

/** Dagen per maand; februari 29, want het jaar vragen we niet. */
export const daysInMonth = (month: number) => [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 31

export const birthdayText = (b: Customer['birthday']) => (b ? `${b.day} ${MONTHS[b.month - 1]}` : 'Niet ingevuld. Vul je hem in, dan krijg je die dag een kleine felicitatie.')

import { countryForPostcode, formatPostcode, isPostcode, isValidEmail } from '../../../core/rules/contact'

export { formatPostcode }

/** Het profiel vraagt geen land: dat volgt uit de postcode (vier cijfers zonder letters = België). */
const anyPostcode = (v: string) => isPostcode(v, 'NL') || isPostcode(v, 'BE')

/** Fout per veld, zodat de melding bij het juiste veld staat. Leeg object = in orde. */
export function profileErrors(section: ProfileSection, form: Customer): Partial<Record<string, string>> {
  if (section === 'persoon') {
    return {
      ...(form.firstName.trim() ? {} : { firstName: 'Je voornaam mag niet leeg zijn.' }),
      ...(isValidEmail(form.email) ? {} : { email: 'Dit e-mailadres klopt niet helemaal.' }),
    }
  }
  if (section === 'adres') {
    const a = form.address
    // Leeg mag (geen adres bewaren). Half ingevuld niet: dan klopt het bij afrekenen niet.
    const any = [a.street, a.houseNumber, a.postcode, a.city].some((v) => v.trim())
    if (!any) return {}
    return {
      ...(a.postcode.trim() && anyPostcode(a.postcode) ? {} : { postcode: 'Een postcode ziet eruit als 1234 AB, of 1000 in België.' }),
      ...(/^\d/.test(a.houseNumber.trim()) ? {} : { houseNumber: 'Een huisnummer begint met een cijfer, bijv. 12 of 12a.' }),
      ...(a.street.trim() ? {} : { street: 'Je straat nog.' }),
      ...(a.city.trim() ? {} : { city: 'Je woonplaats nog.' }),
    }
  }
  if (section === 'verjaardag') {
    const b = form.birthday
    if (!b) return {}
    if (!b.day || !b.month) return { birthday: 'Kies een dag én een maand, of haal je verjaardag weg.' }
    return b.day >= 1 && b.day <= daysInMonth(b.month) ? {} : { birthday: `${MONTHS[b.month - 1] ?? 'Die maand'} heeft geen ${b.day} dagen.` }
  }
  return {}
}

/** Alleen het geopende blok gaat naar de server — nooit het hele klantprofiel. */
export function patchFor(section: ProfileSection, form: Customer): CustomerPatch {
  if (section === 'persoon')
    return {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
    }
  if (section === 'adres')
    return {
      address: {
        street: form.address.street.trim(),
        houseNumber: form.address.houseNumber.trim(),
        postcode: formatPostcode(form.address.postcode),
        city: form.address.city.trim(),
        country: countryForPostcode(form.address.postcode),
      },
    }
  if (section === 'verjaardag') return { birthday: form.birthday ?? null }
  return { payment: form.payment }
}
